// Angular
import { CurrencyPipe, DatePipe, NgClass } from '@angular/common';
import {
  Component,
  OnInit,
  signal,
  ViewChild,
  inject,
  Input,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
// Rxjs
import { lastValueFrom } from 'rxjs';
// PrimeNG
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { SidebarModule } from 'primeng/sidebar';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { SkeletonModule } from 'primeng/skeleton';
import { DialogService } from 'primeng/dynamicdialog';
// Pipes
import { ShippingDatePipe } from '@shared/pipes-v2/shipping-date.pipe';
// Components
import { GroupDateSidebarComponent } from '../group-date-sidebar/group-date-sidebar.component';
//Services
import { CartService } from '@core/services-v2/cart.service';
//Models
import {
  IShoppingCart,
  IShoppingCartGroup,
  IShoppingCartTripDate,
} from '@core/models-v2/cart/shopping-cart.interface';
import { ShippingService } from '@shared/interfaces/address';
//Utils
import { ISession } from '@core/models-v2/auth/session.interface';
import { SessionService } from '@core/services-v2/session/session.service';
import { CartApiService } from '@core/services-v2/cart/cart-api/cart-api.service';
import { ShippingType } from '../../models/shipping-type.type';
import { ReloadModalComponent } from 'src/app/checkout-v2/components/reload-modal/reload-modal.component';
import { SelectDate } from './models/select-date.interface';
import { environment } from '@env/environment';
import { DeliveryType } from '@core/enums/delivery-type.enum';
import { GeolocationServiceV2 } from '@core/services-v2/geolocation/geolocation.service';
import { CallBackCartLoaded } from '@core/models-v2/cart/callback-cart-loaded.type';
import { DateUtils } from './utils/date-utils.service';

const NG_MODULES = [NgClass, CurrencyPipe, FormsModule];
const PRIME_MODULES = [
  ButtonModule,
  TooltipModule,
  SidebarModule,
  RadioButtonModule,
  SkeletonModule,
  ConfirmDialogModule,
  ToastModule,
];
const COMPONENTS = [GroupDateSidebarComponent];
const PIPES = [ShippingDatePipe];

@Component({
  selector: 'app-shipping-product-list',
  standalone: true,
  imports: [NG_MODULES, PRIME_MODULES, COMPONENTS, PIPES],
  templateUrl: './shipping-product-list.component.html',
  styleUrl: './shipping-product-list.component.scss',
  providers: [ConfirmationService, MessageService, DialogService],
})
export class ShippingProductListComponent implements OnInit {
  //#region ViewChild components
  @ViewChild('sidebar') sidebar!: GroupDateSidebarComponent;
  //#endregion

  _shippingType!: ShippingType;

  //#region Inputs
  /**
   * Tipo de entrega de los productos.
   * @param shippingType - Tipo de entrega.
   */
  @Input() set shippingType(shippingType: ShippingType) {
    if (this.isLoggedIn) this.isLoadingShippingGroups.set(true);
    this.shippingGroups = [];
    this._shippingType = shippingType;
  }

  @Input() set addressShipping(value: any) {
    this._addressShipping = value;
    if (!this._addressShipping) {
      this.shippingGroups = [];
      this.isLoadingShippingGroups.set(false);
      return;
    }

    if (this._shippingType === 'PICKUP') {
      const currentAddress = this.geolocationService.getSelectedStore();
      if (currentAddress.code === this._addressShipping.code) {
        this.getPickupShippingGroups();
      } else {
        this.setGlobalSelectStore(
          this._addressShipping,
          async (_: IShoppingCart) => this.getPickupShippingGroups()
        );
      }
    } else this.getDeliveryShippingGroups();
  }
  //#endregion

  _addressShipping!: any;
  isVisibleSidebarInfo: boolean = false;
  isLoadingShippingGroups = signal<boolean>(false);
  shippingGroups: any[] = [];
  session!: ISession;
  isLoggedIn: boolean = false;
  countryCode: string = environment.country;

  //#region Dependencies
  private readonly cartService = inject(CartService);
  private readonly cartApiService = inject(CartApiService);
  private readonly sessionService = inject(SessionService);
  private readonly datePipe = inject(DatePipe);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly dialogService = inject(DialogService);
  private readonly geolocationService = inject(GeolocationServiceV2);
  //#endregion

  //#region Lifecycle Hooks
  ngOnInit(): void {
    this.session = this.sessionService.getSession();
    this.isLoggedIn = this.sessionService.isLoggedIn();
  }
  //#endregion

  /**
   * Seleccionar la fecha de despacho de un grupo de productos.
   * @param selectedDate - Fecha de despacho seleccionada.
   * @param indexGroup - Índice del grupo de productos.
   */
  onSelectDate({ indexGroup, indexSelectedDate }: SelectDate): void {
    const selectedDate =
      this.shippingGroups[indexGroup].dates[indexSelectedDate];
    this.shippingGroups[indexGroup].selectedDate = selectedDate;

    // TODO: pasar esto a un cartService...
    const formattedDate = this.datePipe.transform(
      selectedDate.fecha,
      'EEEE dd MMM'
    );
    const shippingDescription =
      this._shippingType === 'PICKUP'
        ? `Retiro tienda ${formattedDate}`
        : `Entrega ${formattedDate}`;
    this.cartService.addTotalShipping({
      title: shippingDescription,
      price: selectedDate.precio,
      type: 'shipping',
    });
    this.updateGroupDate(indexGroup, selectedDate.index);
  }

  /**
   * Obtener las fechas y grupos de retiro en tienda.
   */
  private async getPickupShippingGroups(): Promise<void> {
    this.isLoadingShippingGroups.set(true);
    this.shippingGroups = [];
    const { username, email } = this.sessionService.getSession();
    try {
      const cart = await lastValueFrom(
        this.cartService.logisticPromise({
          user: username || email,
          deliveryMode: DeliveryType.PICKUP,
          destination: `${this._addressShipping.code}|${this._addressShipping.regionCode}`,
          address: this._addressShipping.name,
          addressId: this._addressShipping.id,
        })
      );
      if (cart.groups?.length) {
        cart.groups.forEach((group: IShoppingCartGroup) => {
          let i = 0;
          let dia_despacho: ShippingService[] = [];
          let totalGroup = 0;
          group.tripDates.forEach((item: IShoppingCartTripDate) => {
            let isSaturday = DateUtils.isSaturday(
              item.requestedDate.toString()
            );
            dia_despacho.push({
              index: i++,
              id: group.id,
              diasdemora: item.businessDays,
              fecha: item.requestedDate,
              fechaPicking: item.pickingDate,
              origen: group.warehouse,
              precio: item.price,
              proveedor: item.carrier.description,
              tipoenvio: 'TIENDA',
              tipopedido: 'VEN- RPTDA',
              isSabado: isSaturday,
            });
          });
          if (this.countryCode === 'es') {
            let totalImpNeto21 = 0;

            group.products.forEach((product) => {
              const iva = product.iva ?? 0.21;
              const netPrice = (product.price || 0) / (1 + iva);

              const precio = Math.round(netPrice * 100) / 100;

              const cantidad = product.quantity || 0;

              const importeNetoProducto =
                Math.round(precio * cantidad * 100) / 100;

              if (iva === 0.21) {
                totalImpNeto21 += importeNetoProducto;
              }
            });

            const subtotal = Math.round(totalImpNeto21 * 100) / 100;
            const taxAmount = Math.round(subtotal * 0.21 * 100) / 100;
            totalGroup = subtotal + taxAmount;
          } else {
            group.products.forEach((product) => {
              totalGroup += product.price * product.quantity;
            });
          }
          this.shippingGroups.push({
            group: group.id,
            products: group.products,
            dates: dia_despacho,
            price: totalGroup,
            selectedDate: dia_despacho[0],
          });
        });
      }
      setTimeout(() => {
        this.isLoadingShippingGroups.set(false);
      }, 1000);
    } catch (error) {
      console.log(error);
      this.shippingGroups = [];
      this.isLoadingShippingGroups.set(false);
    }
  }

  private setGlobalSelectStore(
    addressStore: any,
    callBackCartLoaded: CallBackCartLoaded
  ) {
    this.geolocationService.setSelectedStore({
      zone: addressStore.zone,
      code: addressStore.code,
      city: addressStore.city,
      callBackCartLoaded,
    });
  }

  /**
   * Obtener las fechas y grupos de despacho a domicilio.
   */
  private async getDeliveryShippingGroups(): Promise<void> {
    this.isLoadingShippingGroups.set(true);
    this.shippingGroups = [];
    const { username, email } = this.sessionService.getSession();
    let i = 0;
    try {
      const cart = await lastValueFrom(
        this.cartService.logisticPromise({
          user: username || email,
          deliveryMode: DeliveryType.DELIVERY,
          destination:
            this.countryCode === 'pe' && !this.isLoggedIn
              ? `${this._addressShipping.region
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')}|${
                  this._addressShipping.regionCode
                    ? this._addressShipping.regionCode
                    : ''
                }`
              : `${this._addressShipping.city
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')}|${
                  this._addressShipping.regionCode
                    ? this._addressShipping.regionCode
                    : ''
                }`,
          address: this._addressShipping.address,
          addressId: this._addressShipping.id,
        })
      );
      if (cart.groups?.length) {
        cart.groups.forEach((group: IShoppingCartGroup) => {
          let j = 0;
          let dia_despacho: ShippingService[] = [];
          i = i + 1;
          let totalGroup = 0;
          group.tripDates.forEach((item: IShoppingCartTripDate) => {
            let index = j++;
            dia_despacho.push({
              index,
              id: group.id,
              diasdemora: item.businessDays,
              fecha: item.requestedDate,
              fechaPicking: item.pickingDate,
              origen: group.warehouse,
              precio: item.price,
              proveedor: item.carrier.description,
              tipoenvio: item.serviceType.code,
              tipopedido: item.serviceType.code,
            });
          });
          if (this.countryCode === 'es') {
            let totalImpNeto21 = 0;

            group.products.forEach((product) => {
              const iva = product.iva ?? 0.21;
              const netPrice = (product.price || 0) / (1 + iva);
              const precio = Math.round(netPrice * 100) / 100;
              const cantidad = product.quantity || 0;
              const importeNetoProducto =
                Math.round(precio * cantidad * 100) / 100;

              if (iva === 0.21) {
                totalImpNeto21 += importeNetoProducto;
              }
            });

            const subtotal = Math.round(totalImpNeto21 * 100) / 100;
            const taxAmount = Math.round(subtotal * 0.21 * 100) / 100;
            totalGroup = subtotal + taxAmount;
          } else {
            group.products.forEach((product) => {
              totalGroup += product.price * product.quantity;
            });
          }

          this.shippingGroups.push({
            group: group.id,
            products: group.products,
            dates: dia_despacho,
            price: totalGroup,
            selectedDate: dia_despacho[0],
          });
        });
      } else {
      }
      this.isLoadingShippingGroups.set(false);
    } catch (error) {
      console.log(error);
      this.shippingGroups = [];
      this.isLoadingShippingGroups.set(false);
    }
  }

  /**
   * Guardar la fecha de despacho seleccionada.
   * @param indexGroup - Índice del grupo de productos.
   * @param indexDate - Índice de la fecha de despacho seleccionada.
   */
  private updateGroupDate(indexGroup: number, indexDate: number): void {
    this.cartApiService.updateShippingGroup(indexGroup, indexDate).subscribe({
      error: (error) => console.error(error),
    });
  }

  /**
   * Eliminar grupo de productos del carro de compra.
   * @param index - Índice del grupo de productos a eliminar.
   */
  private deleteGroup(index: number): void {
    const shippingCode =
      this._shippingType === 'PICKUP'
        ? this._addressShipping.code
        : this._addressShipping.city;

    const user = this.isLoggedIn ? this.session.username : this.session.email;
    this.cartService
      .removeGroup({ user, id: index + 1, branch: shippingCode })
      .subscribe({
        next: () => {
          this.cartService.load();
          if (this._shippingType === 'PICKUP') this.getPickupShippingGroups();
          else this.getDeliveryShippingGroups();
          this.messageService.add({
            severity: 'info',
            summary: 'Eliminado',
            detail: 'Grupo eliminado correctamente',
          });
        },
        error: (error) => console.error(error),
      });
  }

  /**
   * Muestra un dialogo de confirmación para eliminar un grupo de productos.
   * @param event - Evento del DOM que contiene la referencia al elemento button.
   * @param index - Índice del grupo de productos a eliminar.
   */
  confirmDeleteGroup(event: Event, index: number): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: '¿Estas seguro que deseas eliminar este grupo?',
      header: 'Eliminar grupo',
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectLabel: 'Cancelar',
      acceptLabel: 'Eliminar',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text p-button-text',
      accept: () => this.deleteGroup(index),
    });
  }

  reloadPromise() {
    this.dialogService.open(ReloadModalComponent, {
      header: 'Aviso',
      width: '50vw',
      modal: true,
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw',
      },
    });
    if (this._shippingType === 'PICKUP') this.getPickupShippingGroups();
    else this.getDeliveryShippingGroups();
  }

  submit(): boolean {
    return !this.isLoadingShippingGroups() && this.shippingGroups.length > 0;
  }
}
